import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Star, Sparkles } from 'lucide-react';

interface NatalChartFormProps {
  onSubmit: (data: { birthDate: string; birthTime: string; birthPlace: string; name: string }) => void;
  isLoading: boolean;
}

export default function NatalChartForm({ onSubmit, isLoading }: NatalChartFormProps) {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!birthDate || !birthTime || !birthPlace) return;
    onSubmit({ birthDate, birthTime, birthPlace, name });
  };

  const isValid = birthDate && birthTime && birthPlace;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full max-w-xl mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-4"
        >
          <Star className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-amber-300 text-xs font-medium tracking-wider uppercase">Carta Natal Hebrea</span>
        </motion.div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">
          <span className="text-white">Descubre tu </span>
          <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
            מַזָּל
          </span>
        </h1>
        <p className="text-slate-400 text-sm leading-relaxed max-w-sm mx-auto">
          Ingresa tu fecha y hora de nacimiento. Calcularemos tu fecha hebrea, mazal, tribu,
          letra del alma y Sefirah dominante.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name (optional) */}
        <div className="glass-panel rounded-xl p-4">
          <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">
            Tu nombre <span className="text-slate-600 normal-case">(opcional)</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="¿Cómo te llamas?"
            className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm"
          />
        </div>

        {/* Date */}
        <div className="glass-panel rounded-xl p-4">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">
            <Calendar className="w-3.5 h-3.5 text-amber-400" />
            Fecha de nacimiento
          </label>
          <input
            type="date"
            value={birthDate}
            onChange={e => setBirthDate(e.target.value)}
            required
            max={new Date().toISOString().split('T')[0]}
            min="1900-01-01"
            className="w-full bg-transparent text-white outline-none text-sm [color-scheme:dark]"
          />
        </div>

        {/* Time */}
        <div className="glass-panel rounded-xl p-4">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">
            <Clock className="w-3.5 h-3.5 text-amber-400" />
            Hora de nacimiento
          </label>
          <input
            type="time"
            value={birthTime}
            onChange={e => setBirthTime(e.target.value)}
            required
            className="w-full bg-transparent text-white outline-none text-sm [color-scheme:dark]"
          />
          {birthTime && parseInt(birthTime.split(':')[0]) >= 19 && (
            <p className="text-amber-400/70 text-xs mt-2 flex items-start gap-1.5">
              <span className="mt-0.5">⚠</span>
              <span>Naciste después de la puesta del sol — en el calendario hebreo ya había comenzado el día siguiente. Calculamos la fecha corregida automáticamente.</span>
            </p>
          )}
        </div>

        {/* Place */}
        <div className="glass-panel rounded-xl p-4">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">
            <MapPin className="w-3.5 h-3.5 text-amber-400" />
            Ciudad de nacimiento
          </label>
          <input
            type="text"
            value={birthPlace}
            onChange={e => setBirthPlace(e.target.value)}
            placeholder="Ciudad, País"
            required
            className="w-full bg-transparent text-white placeholder-slate-600 outline-none text-sm"
          />
        </div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={!isValid || isLoading}
          whileHover={isValid && !isLoading ? { scale: 1.02 } : {}}
          whileTap={isValid && !isLoading ? { scale: 0.98 } : {}}
          className={`
            w-full py-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2
            transition-all duration-200
            ${isValid && !isLoading
              ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-black hover:from-amber-400 hover:to-yellow-300 shadow-lg shadow-amber-500/20'
              : 'bg-slate-800 text-slate-500 cursor-not-allowed'
            }
          `}
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Calculando tu carta...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Revelar mi carta natal hebrea
            </>
          )}
        </motion.button>
      </form>

      {/* Footer note */}
      <p className="text-center text-slate-600 text-xs mt-4">
        Basado en el Séfer Yetzirá • Mazalot hebreos • Tradición cabalística
      </p>
    </motion.div>
  );
}
